import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimesCircle } from '@fortawesome/free-solid-svg-icons';

import { renderableType } from '../proptypes/prop-types';
import { buildClassNames, buildClassNameModifiers } from '../util/utils';

import './token.scss';

/** Block */
const blockBaseClassName = 'token';

/** Element */
const contentClassName = 'token__content';
const removalIconClassName = 'token__removalIcon';

/** Modifier */
const focusedModifier = 'focused';
const clickableModifier = 'clickable';

/**
 * A component that renders content as a simple, removable token
 */
export default class Token extends Component {
  static propTypes = {
    /** @ignore */
    children: renderableType.isRequired,
    /** @ignore */
    className: PropTypes.string,
    /** Indicates whether a token is disabled */
    disabled: PropTypes.bool,
    /** Function executed when a token is clicked */
    onClick: PropTypes.func,
    /** Function executed when a token is removed from its container */
    onRemove: PropTypes.func,
    /** Indicates whether a token is removable */
    removable: PropTypes.bool,
  };

  static defaultProps = { removable: false };

  constructor(props) {
    super(props);
    this.state = { _focused: false };
  }

  componentDidMount() {
    this.toggleClickOutsideEvent(true);
  }

  componentWillUnmount() {
    this.toggleClickOutsideEvent(false);
  }

  handleClickOutside = (event) => {
    if (!this.state.hidden) {
      if (!this.tn || !this.tn.contains(event.target)) {
        this.setState({
          ...this.state,
          _focused: false,
        });
      }
    }
  }

  toggleClickOutsideEvent(enabled) {
    if (enabled) {
      document.addEventListener('click', this.handleClickOutside);
    } else {
      document.removeEventListener('click', this.handleClickOutside);
    }
  }

  handleFocus = () => {
    this.setState({
      ...this.state,
      _focused: true,
    });
  };

  handleBlur = () => {
    this.setState({
      ...this.state,
      _focused: false,
    });
  };

  handleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.setState(
      { ...this.state, _focused: true },
      () => this.props.onClick && this.props.onClick(event),
    );
  };

  handleKeyDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.key === 'Enter') {
      this.setState(
        { ...this.state, _focused: true },
        () => this.props.onClick && this.props.onClick(event),
      );
    }
  };

  handleRemove = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.props.onRemove(event);
  };

  handleRemoveOnKeyDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.key === 'Enter') {
      this.props.onRemove(event);
    }
  };

  render() {
    const {
      children,
      className,
      disabled,
      onClick,
      onRemove,
      removable,
      ...other
    } = this.props;
    const { _focused } = this.state;

    const classNames = buildClassNames(
      className,
      buildClassNameModifiers(
        blockBaseClassName,
        _focused && focusedModifier,
        { disabled },
        { removable },
        onClick && clickableModifier,
      ),
      blockBaseClassName,
    );
    const contentClassNames = buildClassNames(
      buildClassNameModifiers(contentClassName, { removable }),
      contentClassName,
    );

    return (
      <div
        role='button'
        tabIndex={0}
        className={classNames}
        ref={(n) => { this.tn = n; }}
        onClick={!disabled ? this.handleClick : undefined}
        onKeyDown={!disabled ? this.handleKeyDown : undefined}
        onFocus={!disabled ? this.handleFocus : undefined}
        onBlur={!disabled ? this.handleBlur : undefined}
        {...other}
      >
        <span className={contentClassNames}>{children}</span>
        {removable &&
          <span
            role='button'
            tabIndex={0}
            className={removalIconClassName}
            onClick={!disabled ? this.handleRemove : undefined}
            onKeyDown={!disabled ? this.handleRemoveOnKeyDown : undefined}
          >
              <FontAwesomeIcon icon={faTimesCircle} />
            </span>
        }
      </div>
    );
  }
}
